import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase client factory for Care & Key.
 *
 * Two entry points, deliberately:
 *   - createBrowserClient:  for apps/web + apps/field (persists the user session).
 *   - createTokenClient:    for scripts/tests that act as a specific end-user via
 *                           a raw access-token (JWT). Used by the org-isolation
 *                           suite — which must NEVER use the service_role key.
 *
 * There is intentionally no helper that takes the service_role key: RLS must
 * always apply. Admin/seed scripts build their own client explicitly.
 */

export type SupabaseEnv = {
  url: string;
  anonKey: string;
};

/** Browser client — persists + refreshes the signed-in user's session. */
export function createBrowserClient(env: SupabaseEnv): SupabaseClient {
  return createClient(env.url, env.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
}

/**
 * Token client — acts as one end-user identified by `accessToken` (a real JWT).
 * No session persistence. RLS applies exactly as it would for that user.
 */
export function createTokenClient(env: SupabaseEnv, accessToken: string): SupabaseClient {
  return createClient(env.url, env.anonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  });
}

/** Reads a browser-exposed Supabase env from Vite's import.meta.env. */
export function readViteSupabaseEnv(env: Record<string, string | undefined>): SupabaseEnv {
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error(
      'Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. Copy .env.example to .env.',
    );
  }
  return { url, anonKey };
}
